import type { Koi } from '@ksyos/koi';
import { useState } from 'react';
import type { ValidationErrors } from '@frontend/utils';
import { emptyValidationErrors, updateValidationErrorsForOne, validate as utilValidate } from '@frontend/utils';

export function useFormData<TFormData extends Record<string, unknown>>(
    schema: ReturnType<typeof Koi.object>,
    initialValue?: TFormData
) {
    const [formData, setFormData] = useState<TFormData>(initialValue ?? ({} as TFormData));
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const schemaContainer = { schema };

    function reset() {
        setFormData(initialValue ?? ({} as TFormData));
        setErrors(emptyValidationErrors);
    }

    function update(value: Partial<TFormData>) {
        setFormData({ ...formData, ...value });

        const updateErrors = errors.withMutations((map) => {
            for (const key in errors.toObject()) {
                const result = updateValidationErrorsForOne(schemaContainer, value, errors, key);

                if (!result.has(key)) {
                    map.remove(key);
                }
            }
        });

        setErrors(updateErrors);
    }

    function validate() {
        const validated = utilValidate(schemaContainer, formData);

        setErrors(validated.errors);

        return {
            ...validated,
            hasErrors: validated.errors.size > 0,
        };
    }

    function hasChanges() {
        return JSON.stringify(formData) !== JSON.stringify(initialValue);
    }

    return { errors, formData, hasChanges, reset, update, validate };
}
